import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Divider,
  IconButton,
  FormControlLabel,
  CircularProgress,
  Switch,
  InputAdornment,
  useTheme,
  Modal,
  Fade,
  Backdrop,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CloudUpload, Delete, Close } from "@mui/icons-material";

const PersonalInfoStep = ({
  profileData,
  handleInputChange,
  handleResumeUpload,
  isUploadingResume,
  handleFileRemove,
  isSameAddress,
  handleSameAddressToggle,
  fetchAddressFromPincode,
  isFetchingPincode,
  validationErrors,
}) => {
  const theme = useTheme();
  const [photoPreview, setPhotoPreview] = useState(null);
  // ADDED: State to control the resume modal
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);

  // ADDED: Handlers for opening and closing the modal
  const handleOpenResumeModal = () => setIsResumeModalOpen(true);
  const handleCloseResumeModal = () => setIsResumeModalOpen(false);

  useEffect(() => {
    if (profileData.profile_photo instanceof File) {
      const objectUrl = URL.createObjectURL(profileData.profile_photo);
      setPhotoPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (profileData.profile_photo_url) {
      setPhotoPreview(profileData.profile_photo_url);
    } else {
      setPhotoPreview(null);
    }
  }, [profileData.profile_photo, profileData.profile_photo_url]);

  const hasPhoto =
    !!profileData.profile_photo || !!profileData.profile_photo_url;

  const paperSectionStyles = {
    p: 3,
    mb: 4,
    borderRadius: "10px",
  };

  // ADDED: Style for the modal content
  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "95%", sm: "80%", md: "70%" },
    maxWidth: "900px",
    height: "90vh",
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 3,
    display: "flex",
    flexDirection: "column",
  };

  return (
    <Box sx={{ bgcolor: "background.paper", p: 0 }}>
      <Paper sx={{
        ...paperSectionStyles,
        bgcolor: "background.paper",
        border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
        borderRadius: "20px",
      }}>

        <Typography variant="h6" gutterBottom sx={{ color: "primary.main" }}>
          Upload Your Resume (to auto-fill all fields)
        </Typography>

        {profileData.ui_documents.resume_url ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              p: "14px",
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              mb: 2,
              bgcolor: theme.palette.mint.main,
            }}
          >
            {/* CHANGED: This button now opens the modal */}
            <Button
              onClick={handleOpenResumeModal}
              color="primary"
              sx={{ textTransform: "none", p: 0 }}
            >
              Resume uploaded successfully. View file.
            </Button>
            <IconButton
              onClick={() => handleFileRemove("resume")}
              color="error"
              size="small"
            >
              <Delete />
            </IconButton>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
            }}
          >
            <Button
              variant="contained"
              component="label"
              startIcon={
                isUploadingResume ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CloudUpload />
                )
              }
              disabled={isUploadingResume}
              color="primary"
              sx={{
                textTransform: "none",
                borderRadius: "12px",
                px: 3,
                boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              {isUploadingResume ? "Processing..." : "Upload Resume"}

              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handleInputChange("ui_documents", {
                      ...profileData.ui_documents,
                      resume: file,
                    });
                    handleResumeUpload(file);
                  }
                }}
              />
            </Button>
            {isUploadingResume && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} color="primary" />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Extracting data from resume...
                </Typography>
              </Box>
            )}
          </Box>
        )}
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>
          Select a resume file (.pdf, .doc, .docx) and it will automatically
          upload and populate all form fields with extracted information.
        </Typography>
      </Paper>

      <Divider
        sx={{ mb: 3, "&::before, &::after": { borderColor: "primary.main" } }}
      >
        <Chip
          label="Or Fill Manually"
          variant="outlined"
          sx={{ color: "primary.main", borderColor: "primary.main" }}
        />
      </Divider>

      <Paper sx={{
        ...paperSectionStyles,
        bgcolor: "background.paper",
        border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
        borderRadius: "20px",
      }}>
        <Typography
          variant="subtitle1"
          sx={{ color: "primary.main", fontWeight: "bold", mb: 2 }}
        >
          Profile Photo{" "}
          <span style={{ color: theme.palette.error.main }}>*</span>
        </Typography>


        {photoPreview && (
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Box
              component="img"
              sx={{
                height: 120,
                width: 120,
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid",
                borderColor: "divider",
              }}
              alt="Profile Preview"
              src={photoPreview}
            />
          </Box>
        )}

        <Button
          variant="outlined"
          component="label"
          startIcon={<CloudUpload />}
          fullWidth
          color={hasPhoto ? "primary" : "error"}
          sx={{ mt: 2, borderRadius: "12px" }}
        >

          {hasPhoto ? "Change Profile Photo" : "Upload Profile Photo"}
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                handleInputChange("profile_photo", file);
              }
            }}
          />
        </Button>
        {validationErrors.profile_photo && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 1, display: "block" }}
          >
            {validationErrors.profile_photo}
          </Typography>
        )}
      </Paper>

      <Paper sx={{
        ...paperSectionStyles,
        bgcolor: "background.paper",
        border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
        borderRadius: "20px",
      }}>
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ color: "primary.main", fontWeight: "bold", mb: 2 }}
        >
          Basic Information
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Full Name"
              value={profileData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              required
              size="small"
              error={!!validationErrors.name}
              helperText={validationErrors.name || ""}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Email ID"
              type="email"
              value={profileData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              required
              size="small"
              error={!!validationErrors.email}
              helperText={validationErrors.email || ""}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Mobile No"
              value={profileData.phone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                handleInputChange("phone", value);
              }}
              size="small"
              required
              error={!!validationErrors.phone}
              helperText={validationErrors.phone || ""}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Current Company"
              value={profileData.current_company}
              onChange={(e) =>
                handleInputChange("current_company", e.target.value)
              }
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{
        ...paperSectionStyles,
        bgcolor: "background.paper",
        border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
        borderRadius: "20px",
      }}>
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ color: "primary.main", fontWeight: "bold", mb: 2 }}
        >
          Current Address
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Pincode"
              value={profileData.current_pincode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                handleInputChange("current_pincode", value);
                if (value.length === 6) {
                  fetchAddressFromPincode(value, "current");
                }
              }}
              size="small"
              required
              error={!!validationErrors.current_pincode}
              helperText={validationErrors.current_pincode || ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {isFetchingPincode.current && (
                      <CircularProgress size={20} />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Country"
              value={profileData.current_country}
              onChange={(e) =>
                handleInputChange("current_country", e.target.value)
              }
              size="small"
              required
              error={!!validationErrors.current_country}
              helperText={validationErrors.current_country || ""}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="State"
              value={profileData.current_state}
              onChange={(e) =>
                handleInputChange("current_state", e.target.value)
              }
              size="small"
              required
              error={!!validationErrors.current_state}
              helperText={validationErrors.current_state || ""}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="District"
              value={profileData.current_district}
              onChange={(e) =>
                handleInputChange("current_district", e.target.value)
              }
              size="small"
              required
              error={!!validationErrors.current_district}
              helperText={validationErrors.current_district || ""}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="City"
              value={profileData.current_city}
              onChange={(e) =>
                handleInputChange("current_city", e.target.value)
              }
              size="small"
              required
              error={!!validationErrors.current_city}
              helperText={validationErrors.current_city || ""}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Area/Locality"
              value={profileData.current_area}
              onChange={(e) =>
                handleInputChange("current_area", e.target.value)
              }
              size="small"
              required
              error={!!validationErrors.current_area}
              helperText={validationErrors.current_area || ""}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{
        ...paperSectionStyles,
        bgcolor: "background.paper",
        border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
        borderRadius: "20px",
        mb: 0
      }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography
            variant="subtitle1"
            sx={{ color: "primary.main", fontWeight: "bold" }}
          >
            Permanent Address
          </Typography>
          <FormControlLabel
            sx={{ color: "text.primary", ml: 0 }}
            labelPlacement="start"
            control={
              <Switch
                checked={isSameAddress}
                onChange={handleSameAddressToggle}
                name="sameAsCurrent"
                size="small"
                color="primary"
              />
            }
            label="Same as Current Address"
          />
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              disabled={isSameAddress}
              fullWidth
              label="Pincode"
              value={profileData.permanent_pincode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                handleInputChange("permanent_pincode", value);
                if (value.length === 6) {
                  fetchAddressFromPincode(value, "permanent");
                }
              }}
              size="small"
              required={!isSameAddress}
              error={!isSameAddress && !!validationErrors.permanent_pincode}
              helperText={
                !isSameAddress ? validationErrors.permanent_pincode || "" : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {isFetchingPincode.permanent && (
                      <CircularProgress size={20} />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              disabled={isSameAddress}
              fullWidth
              label="Country"
              value={profileData.permanent_country}
              onChange={(e) =>
                handleInputChange("permanent_country", e.target.value)
              }
              size="small"
              required={!isSameAddress}
              error={!isSameAddress && !!validationErrors.permanent_country}
              helperText={
                !isSameAddress ? validationErrors.permanent_country || "" : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              disabled={isSameAddress}
              fullWidth
              label="State"
              value={profileData.permanent_state}
              onChange={(e) =>
                handleInputChange("permanent_state", e.target.value)
              }
              size="small"
              required={!isSameAddress}
              error={!isSameAddress && !!validationErrors.permanent_state}
              helperText={
                !isSameAddress ? validationErrors.permanent_state || "" : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              disabled={isSameAddress}
              fullWidth
              label="District"
              value={profileData.permanent_district}
              onChange={(e) =>
                handleInputChange("permanent_district", e.target.value)
              }
              size="small"
              required={!isSameAddress}
              error={!isSameAddress && !!validationErrors.permanent_district}
              helperText={
                !isSameAddress ? validationErrors.permanent_district || "" : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              disabled={isSameAddress}
              fullWidth
              label="City"
              value={profileData.permanent_city}
              onChange={(e) =>
                handleInputChange("permanent_city", e.target.value)
              }
              size="small"
              required={!isSameAddress}
              error={!isSameAddress && !!validationErrors.permanent_city}
              helperText={
                !isSameAddress ? validationErrors.permanent_city || "" : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              disabled={isSameAddress}
              fullWidth
              label="Area/Locality"
              value={profileData.permanent_area}
              onChange={(e) =>
                handleInputChange("permanent_area", e.target.value)
              }
              size="small"
              required={!isSameAddress}
              error={!isSameAddress && !!validationErrors.permanent_area}
              helperText={
                !isSameAddress ? validationErrors.permanent_area || "" : ""
              }
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ADDED: Resume viewer modal */}
      <Modal
        open={isResumeModalOpen}
        onClose={handleCloseResumeModal}
        aria-labelledby="resume-modal-title"
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={isResumeModalOpen}>
          <Box sx={{
            ...modalStyle,
            borderRadius: "24px",
            border: theme => `1px solid ${theme.palette.primary.main}`,
            overflow: "hidden",
            boxShadow: theme => `0 24px 48px ${alpha(theme.palette.common.black, 0.4)}`,
          }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                flexShrink: 0,
                p: 1
              }}
            >

              <Typography id="resume-modal-title" variant="h6" component="h2">
                Resume Preview
              </Typography>
              <IconButton onClick={handleCloseResumeModal} aria-label="close">
                <Close />
              </IconButton>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
              <iframe
                src={profileData.ui_documents.resume_url}
                style={{ width: "100%", height: "100%", border: "none" }}
                title="Resume Preview"
              />
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default PersonalInfoStep;
